import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import App from "./App";
import "./index.css";
import { ensureContent } from "./db/seed";
import { ErrorBoundary } from "./components/ErrorBoundary";

// Global error capture — catches everything that ErrorBoundary doesn't (async,
// event handlers, dynamic import failures). Persisted under pp.lastError for
// post-mortem when the user reports a white-screen.
window.addEventListener("error", (e) => {
  try {
    localStorage.setItem(
      "pp.lastError",
      JSON.stringify({
        source: "window.onerror",
        message: e.message,
        filename: e.filename,
        lineno: e.lineno,
        colno: e.colno,
        stack: e.error?.stack ?? null,
        at: new Date().toISOString(),
        url: window.location.href,
      }),
    );
  } catch {
    /* ignore */
  }
});
window.addEventListener("unhandledrejection", (e) => {
  try {
    const reason = e.reason as { message?: string; stack?: string } | string;
    localStorage.setItem(
      "pp.lastError",
      JSON.stringify({
        source: "unhandledrejection",
        message:
          typeof reason === "string"
            ? reason
            : reason?.message ?? String(reason),
        stack: typeof reason === "object" ? reason?.stack ?? null : null,
        at: new Date().toISOString(),
        url: window.location.href,
      }),
    );
  } catch {
    /* ignore */
  }
});

// Recover from vite chunk-load failures (happens when the SW served an old
// index.html that points to a hashed bundle the server no longer has). One
// retry guarded by sessionStorage flag to avoid reload loops.
window.addEventListener("error", (e) => {
  const isChunkError =
    e.message?.includes("Failed to fetch dynamically imported module") ||
    e.message?.includes("Loading chunk") ||
    (e.filename && /\/assets\/.*\.js$/.test(e.filename));
  if (!isChunkError) return;
  if (sessionStorage.getItem("pp.chunkRetried") === "1") return;
  sessionStorage.setItem("pp.chunkRetried", "1");
  console.warn("[recovery] chunk load failure — reloading once");
  window.location.reload();
});

// Ensure IndexedDB is seeded with packs/cards before mounting.
// The promise resolves quickly on warm starts; the splash screen handles cold.
ensureContent().catch((e) => console.error("[seed] failed", e));

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </ErrorBoundary>
  </React.StrictMode>,
);
